import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { CampaignType } from 'apis/restaurant-api/merchant-admin/campaigns/types';
import { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';
import { useLocalStorageJSON } from 'common/hooks';
import useCampaignTypesPermittedToEdit from 'modules/campaigns/hooks/useCampaignTypesPermittedToEdit';
import { useIsOfflineHourEnabled } from 'modules/insights-v2/hooks/useIsOfflineHourEnabled';

import { NavigationItem } from './types';
import { useSelfDeliveryAreasLaunchTooltip } from './useSelfDeliveryAreasLaunchTooltip';
import { useIsOffersEnabled } from '../insights-v2/components/ratings/CompensationOffers/useCompensationOffers';
import useNewAlivenessFeatureFlag from '../insights-v2/hooks/useAlivenessFeatureFlag';

type Tooltip = {
  navigation: NavigationItem;
  text: string;
};

const tooltipsOrder = [
  'campaigns-free-delivery',
  'insights-v2-operations-aliveness',
  'campaigns-basket-discount',
  'campaigns-item-discount',
  'ratings-and-reviews',
  'offline-by-hour',
  'setting-vat',
  'ratings-concessions',
  'end-of-day-campaigns',
  'self-delivery-areas',
] as const;

type TooltipId = (typeof tooltipsOrder)[number];

// this intermediate object is needed for an additional type safety to ensure that all tooltips are defined
const tooltipsExhaustiveDefinitions: Record<TooltipId, Tooltip> = {
  'campaigns-free-delivery': {
    navigation: 'CAMPAIGNS',
    text: 'navigation.tooltips.campaigns.free-delivery',
  },
  'insights-v2-operations-aliveness': {
    navigation: 'INSIGHTS_V2_OPERATIONS',
    text: 'navigation.tooltips.campaigns.aliveness',
  },
  'campaigns-basket-discount': {
    navigation: 'CAMPAIGNS',
    text: 'navigation.tooltips.campaigns.basket-discount',
  },
  'campaigns-item-discount': {
    navigation: 'CAMPAIGNS',
    text: 'navigation.tooltips.campaigns.item-discount',
  },
  'ratings-and-reviews': {
    navigation: 'INSIGHTS_V2_RATINGS_AND_REVIEWS',
    text: 'navigation.tooltips.campaigns.ratings-and-reviews',
  },
  'offline-by-hour': {
    navigation: 'INSIGHTS_V2_OPERATIONS',
    text: 'navigation.tooltips.campaigns.offline-by-hour',
  },
  'setting-vat': {
    navigation: 'INSIGHTS_V2',
    text: 'navigation.tooltips.campaigns.setting-vat',
  },
  'ratings-concessions': {
    navigation: 'INSIGHTS_V2_RATINGS_AND_REVIEWS',
    text: 'navigation.tooltips.campaigns.ratings-concessions',
  },
  'end-of-day-campaigns': {
    navigation: 'CAMPAIGNS',
    text: 'navigation.tooltips.campaigns.end-of-day',
  },
  'self-delivery-areas': {
    navigation: 'VENUE_DETAILS',
    text: 'navigation.tooltips.self-delivery-areas',
  },
};

const tooltipsMap: Map<TooltipId, Tooltip> = new Map(
  Object.entries(tooltipsExhaustiveDefinitions) as [TooltipId, Tooltip][],
);

const useLaunchTooltip = (venue: Venue) => {
  const intl = useIntl();
  const [visibleTooltipId, setVisibleTooltipId] = useState<TooltipId | undefined>(undefined);
  const [queueIdx, setQueueIdx] = useState<number>(0);
  const [shownTooltips, setShownTooltips] = useLocalStorageJSON<Partial<Record<TooltipId, boolean>>>(
    'shownTooltips',
    {},
  );

  const campaignTypesEditPermissions = useCampaignTypesPermittedToEdit({
    feature: 'venue',
    featureId: venue.id,
    city: venue.city,
    entityCountry: venue.country,
    productLine: venue.product_line,
  });
  const hasDeliveryDiscountEditPermission = campaignTypesEditPermissions.campaignTypesPermittedToEdit.includes(
    CampaignType.DELIVERY_DISCOUNT,
  );
  const hasBasketDiscountEditPermission = campaignTypesEditPermissions.campaignTypesPermittedToEdit.includes(
    CampaignType.BASKET_DISCOUNT,
  );
  const hasItemDiscountEditPermission = campaignTypesEditPermissions.campaignTypesPermittedToEdit.includes(
    CampaignType.ITEM_DISCOUNT,
  );
  const hasEndOfDayEditPermission = campaignTypesEditPermissions.campaignTypesPermittedToEdit.includes(
    CampaignType.END_OF_DAY,
  );
  const alivenessFeatureFlag = useNewAlivenessFeatureFlag('venue', venue.id);
  const offlineHourEnabled = useIsOfflineHourEnabled('venue');
  const concessionsEnabled = useIsOffersEnabled(venue);
  const selfDeliveryAreasTooltip = useSelfDeliveryAreasLaunchTooltip(venue);

  const featureFlaggedTooltipsMap = useMemo(
    (): Partial<Record<TooltipId, { isDisabled: boolean; isLoading: boolean }>> => ({
      'campaigns-free-delivery': {
        isDisabled: !hasDeliveryDiscountEditPermission,
        isLoading: campaignTypesEditPermissions.isLoading,
      },
      'campaigns-basket-discount': {
        isDisabled: !hasBasketDiscountEditPermission,
        isLoading: campaignTypesEditPermissions.isLoading,
      },
      'campaigns-item-discount': {
        isDisabled: !hasItemDiscountEditPermission,
        isLoading: campaignTypesEditPermissions.isLoading,
      },
      'insights-v2-operations-aliveness': {
        isDisabled: !alivenessFeatureFlag.isNewAlivenessEnabled,
        isLoading: alivenessFeatureFlag.isLoading,
      },
      'offline-by-hour': {
        isDisabled: !offlineHourEnabled.isOfflineHourEnabled,
        isLoading: offlineHourEnabled.isLoading,
      },
      'ratings-concessions': {
        isDisabled: !concessionsEnabled.isOffersEnabled,
        isLoading: concessionsEnabled.isLoading,
      },
      'end-of-day-campaigns': {
        isDisabled: !hasEndOfDayEditPermission,
        isLoading: campaignTypesEditPermissions.isLoading,
      },
      'self-delivery-areas': {
        isDisabled: selfDeliveryAreasTooltip.isDisabled,
        isLoading: selfDeliveryAreasTooltip.isLoading,
      },
    }),
    [
      hasDeliveryDiscountEditPermission,
      campaignTypesEditPermissions.isLoading,
      hasBasketDiscountEditPermission,
      hasItemDiscountEditPermission,
      alivenessFeatureFlag.isNewAlivenessEnabled,
      alivenessFeatureFlag.isLoading,
      offlineHourEnabled.isLoading,
      offlineHourEnabled.isOfflineHourEnabled,
      concessionsEnabled.isOffersEnabled,
      concessionsEnabled.isLoading,
      hasEndOfDayEditPermission,
      selfDeliveryAreasTooltip.isDisabled,
      selfDeliveryAreasTooltip.isLoading,
    ],
  );

  const queuedTooltipId = tooltipsOrder[queueIdx];

  useEffect(() => {
    if (!tooltipsMap.get(queuedTooltipId)) {
      // If there are no more tooltips to show, we hide the tooltip
      setVisibleTooltipId(undefined);
      return;
    }

    if (shownTooltips[queuedTooltipId] !== undefined) {
      // If the tooltip has already been shown, we skip it and move on to the next one
      setQueueIdx(queueIdx + 1);
      return;
    }

    if (featureFlaggedTooltipsMap[queuedTooltipId]?.isDisabled) {
      if (featureFlaggedTooltipsMap[queuedTooltipId]?.isLoading) {
        // If the feature flag is still loading, we don't show any tooltip and wait for loading to finish
        setVisibleTooltipId(undefined);
        return;
      }
      // If the feature flag is disabled, we skip this tooltip and move on to the next one
      setQueueIdx(queueIdx + 1);
      return;
    }

    setVisibleTooltipId(queuedTooltipId);
  }, [featureFlaggedTooltipsMap, queueIdx, queuedTooltipId, shownTooltips]);

  const dismiss = () => {
    if (visibleTooltipId) {
      setShownTooltips({ ...shownTooltips, [visibleTooltipId]: true });
    }
  };

  const visibleTooltip = visibleTooltipId ? tooltipsMap.get(visibleTooltipId) : undefined;

  return {
    tooltipText: visibleTooltip ? intl.formatMessage({ id: visibleTooltip.text }) : '',
    navigationTooltipKey: visibleTooltip?.navigation,
    dismiss,
  };
};

export default useLaunchTooltip;
